import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ProfileCard from './components/ProfileCard';
import OrderedServices from './components/OrderedServices';
import TemplatesTab from './components/TemplatesTab';

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [myApps, setMyApps] = useState<any[]>([]);

  // Load saved apps from localStorage
  useEffect(() => {
    const savedApps = JSON.parse(localStorage.getItem('myApps') || '[]');
    if (savedApps.length > 0) {
      setMyApps(savedApps);
    } else {
      // Default mock apps if none saved
      setMyApps([
        {
          id: '1',
          name: 'Fitness Tracker Pro',
          screens: 12,
          lastEdited: '2 hours ago',
          published: true,
          thumbnail: 'https://public.readdy.ai/ai/img_res/8e8ba96bbc511cff759f9ba0815d46f3.jpg'
        },
        {
          id: '2',
          name: 'Food Delivery Hub',
          screens: 11,
          lastEdited: '1 day ago',
          published: true,
          thumbnail: 'https://readdy.ai/api/search-image?query=food%20delivery%20mobile%20app%20interface%20showing%20restaurant%20menus%20delivery%20tracking%20with%20vibrant%20colors%20and%20appetizing%20food%20images&width=300&height=600&seq=app002&orientation=portrait'
        },
        {
          id: '3',
          name: 'Shopping Mall',
          screens: 8,
          lastEdited: '3 days ago',
          published: false,
          thumbnail: 'https://readdy.ai/api/search-image?query=fashion%20ecommerce%20mobile%20app%20interface%20showing%20clothing%20products%20shopping%20cart%20with%20elegant%20modern%20design%20and%20product%20photography&width=300&height=600&seq=app003&orientation=portrait'
        }
      ]);
    }
  }, [activeTab]);

  const stats = [
    { label: 'Apps Created', value: myApps.length.toString(), icon: 'ri-smartphone-line', color: 'bg-teal-500' },
    { label: 'Templates Used', value: '5', icon: 'ri-layout-grid-line', color: 'bg-orange-500' },
    { label: 'Published Apps', value: myApps.filter(app => app.published).length.toString(), icon: 'ri-rocket-line', color: 'bg-green-500' },
    { label: 'Storage Used', value: '2.4 GB', icon: 'ri-database-2-line', color: 'bg-indigo-500' }
  ];

  const navigationItems = [
    { id: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { id: 'my-apps', label: 'My Apps', icon: 'ri-smartphone-line' },
    { id: 'templates', label: 'Templates', icon: 'ri-layout-grid-line' },
    { id: 'builder', label: 'Builder', icon: 'ri-tools-line' },
    { id: 'billing', label: 'Billing', icon: 'ri-bank-card-line' },
    { id: 'support', label: 'Support', icon: 'ri-customer-service-2-line' }
  ];

  const handleNavigation = (tabId: string) => {
    if (tabId === 'builder') {
      window.REACT_APP_NAVIGATE('/builder');
    } else {
      setActiveTab(tabId);
    }
  };

  const handleDeleteApp = (appId: string) => {
    const savedApps = JSON.parse(localStorage.getItem('myApps') || '[]');
    const filtered = savedApps.filter((app: any) => app.id !== appId);
    localStorage.setItem('myApps', JSON.stringify(filtered));
    setMyApps(filtered);
  };

  const handleEditApp = (appId: string) => {
    const app = myApps.find(a => a.id === appId);
    if (app && app.data) {
      // Store the app data to be loaded in builder
      sessionStorage.setItem('loadApp', JSON.stringify(app.data));
      window.REACT_APP_NAVIGATE('/builder');
    } else {
      window.REACT_APP_NAVIGATE('/builder');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      
      <div className="flex-1 flex pt-20">
        {/* Sidebar */}
        <aside className={`${isSidebarCollapsed ? 'w-20' : 'w-64'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col`}>
          {/* Sidebar Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            {!isSidebarCollapsed && (
              <h2 className="text-lg font-bold text-gray-900">App Wizard Hub</h2>
            )}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
            >
              <i className={`${isSidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-xl text-gray-600 w-5 h-5 flex items-center justify-center`}></i>
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4">
            <ul className="space-y-2">
              {navigationItems.map((item) => (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigation(item.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === item.id
                        ? 'bg-teal-50 text-teal-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${item.icon} text-xl w-5 h-5 flex items-center justify-center`}></i>
                    {!isSidebarCollapsed && <span className="font-medium">{item.label}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-200">
            <div className={`flex items-center gap-3 ${isSidebarCollapsed ? 'justify-center' : ''}`}>
              <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                JD
              </div>
              {!isSidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">John Doe</p>
                  <p className="text-xs text-gray-500 truncate">john@example.com</p>
                </div>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto p-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <>
                {/* Create New App Hero */}
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-2xl p-8 mb-8 text-white">
                  <h1 className="text-3xl font-bold mb-3">Create Your Next App</h1>
                  <p className="text-teal-50 mb-6 text-lg">Start building with a template or from scratch</p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setActiveTab('templates')}
                      className="bg-white text-teal-600 px-6 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-layout-grid-line mr-2 w-5 h-5 inline-flex items-center justify-center"></i>
                      Start from Template
                    </button>
                    <button
                      onClick={() => window.REACT_APP_NAVIGATE('/builder')}
                      className="bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-add-line mr-2 w-5 h-5 inline-flex items-center justify-center"></i>
                      Start from Scratch
                    </button>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {stats.map((stat, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 border border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className={`${stat.color} w-12 h-12 rounded-lg flex items-center justify-center`}>
                          <i className={`${stat.icon} text-2xl text-white w-6 h-6 flex items-center justify-center`}></i>
                        </div>
                      </div>
                      <h3 className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                      <p className="text-sm text-gray-600">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="space-y-3">
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/builder')}
                        className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-add-circle-line text-2xl text-teal-500 w-6 h-6 flex items-center justify-center"></i>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">New App</p>
                          <p className="text-sm text-gray-600">Start building a new app</p>
                        </div>
                      </button>
                      <button
                        onClick={() => setActiveTab('templates')}
                        className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
                      >
                        <i className="ri-layout-grid-line text-2xl text-orange-500 w-6 h-6 flex items-center justify-center"></i>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">Browse Templates</p>
                          <p className="text-sm text-gray-600">Explore 100+ templates</p>
                        </div>
                      </button>
                      <button className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                        <i className="ri-file-copy-line text-2xl text-indigo-500 w-6 h-6 flex items-center justify-center"></i>
                        <div className="text-left">
                          <p className="font-semibold text-gray-900">View Documentation</p>
                          <p className="text-sm text-gray-600">Learn how to use the builder</p>
                        </div>
                      </button>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Activity</h3>
                    <div className="space-y-4">
                      {myApps.slice(0, 3).map((app, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`w-8 h-8 ${idx === 0 ? 'bg-green-100' : idx === 1 ? 'bg-teal-100' : 'bg-orange-100'} rounded-full flex items-center justify-center flex-shrink-0`}>
                            <i className={`${idx === 0 ? 'ri-check-line text-green-600' : idx === 1 ? 'ri-edit-line text-teal-600' : 'ri-add-line text-orange-600'} w-4 h-4 flex items-center justify-center`}></i>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{idx === 0 ? 'Published' : idx === 1 ? 'Updated' : 'Created'} {app.name}</p>
                            <p className="text-xs text-gray-500">{app.lastEdited}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* My Apps Tab */}
            {activeTab === 'my-apps' && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">My Apps</h2>
                    <p className="text-gray-600">Manage and edit your created apps</p>
                  </div>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/builder')}
                    className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line mr-2 w-5 h-5 inline-flex items-center justify-center"></i>
                    Create New App
                  </button>
                </div>

                {myApps.length === 0 ? (
                  <div className="bg-white rounded-xl p-12 border border-gray-200 text-center">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-smartphone-line text-4xl text-gray-400"></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No apps yet</h3>
                    <p className="text-gray-600 mb-6">Start building your first app from a template or from scratch</p>
                    <div className="flex gap-3 justify-center">
                      <button
                        onClick={() => setActiveTab('templates')}
                        className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Browse Templates
                      </button>
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/builder')}
                        className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Start from Scratch
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myApps.map((app) => (
                      <div key={app.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all group">
                        <div className="relative bg-gray-50 aspect-[9/16] overflow-hidden">
                          <img
                            src={app.thumbnail}
                            alt={app.name}
                            className="w-full h-full object-cover object-top"
                          />
                          {app.published && (
                            <div className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                              Published
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                            <button
                              onClick={() => handleEditApp(app.id)}
                              className="bg-teal-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal-600 transition-colors whitespace-nowrap"
                            >
                              Edit
                            </button>
                            <button className="bg-white text-gray-900 px-6 py-2.5 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap">
                              Preview
                            </button>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 flex-1">{app.name}</h3>
                            <button
                              onClick={() => handleDeleteApp(app.id)}
                              className="w-7 h-7 flex items-center justify-center hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span className="flex items-center">
                              <i className="ri-layout-grid-line mr-1.5 w-4 h-4 flex items-center justify-center"></i>
                              {app.screens} screens
                            </span>
                            <span>Edited {app.lastEdited}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* Templates Tab */}
            {activeTab === 'templates' && <TemplatesTab />}

            {/* Billing Tab */}
            {activeTab === 'billing' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Billing & Subscription</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Current Plan</h3>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-2xl font-bold text-gray-900">Pro Plan</p>
                        <p className="text-sm text-gray-600">$29/month</p>
                      </div>
                      <span className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-semibold">
                        Active
                      </span>
                    </div>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-teal-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Unlimited apps
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-teal-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                        100+ templates
                      </li>
                      <li className="flex items-center text-sm text-gray-700">
                        <i className="ri-check-line text-teal-500 mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Priority support
                      </li>
                    </ul>
                    <button className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                      Manage Subscription
                    </button>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Payment Method</h3>
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                        <i className="ri-bank-card-line text-2xl text-white w-6 h-6 flex items-center justify-center"></i>
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">•••• •••• •••• 4242</p>
                        <p className="text-sm text-gray-600">Expires 12/25</p>
                      </div>
                      <button className="text-teal-500 hover:text-teal-600 font-medium text-sm cursor-pointer whitespace-nowrap">
                        Edit
                      </button>
                    </div>
                    <button className="w-full bg-gray-100 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap">
                      Add Payment Method
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Invoice History</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          { date: 'Jan 1, 2025', desc: 'Pro Plan - Monthly', amount: '$29.00', status: 'Paid' },
                          { date: 'Dec 1, 2024', desc: 'Pro Plan - Monthly', amount: '$29.00', status: 'Paid' },
                          { date: 'Nov 1, 2024', desc: 'Pro Plan - Monthly', amount: '$29.00', status: 'Paid' }
                        ].map((invoice, index) => (
                          <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 text-sm text-gray-900">{invoice.date}</td>
                            <td className="py-3 px-4 text-sm text-gray-700">{invoice.desc}</td>
                            <td className="py-3 px-4 text-sm font-semibold text-gray-900">{invoice.amount}</td>
                            <td className="py-3 px-4">
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                                {invoice.status}
                              </span>
                            </td>
                            <td className="py-3 px-4 text-right">
                              <button className="text-teal-500 hover:text-teal-600 text-sm font-medium cursor-pointer whitespace-nowrap">
                                Download
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {/* Support Tab */}
            {activeTab === 'support' && (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Support Center</h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
                    <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-chat-3-line text-3xl text-teal-500 w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Live Chat</h3>
                    <p className="text-sm text-gray-600 mb-4">Chat with our support team</p>
                    <button className="bg-teal-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap">
                      Start Chat
                    </button>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-mail-line text-3xl text-orange-500 w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Email Support</h3>
                    <p className="text-sm text-gray-600 mb-4">Get help via email</p>
                    <button className="bg-orange-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap">
                      Send Email
                    </button>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200 text-center">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-book-open-line text-3xl text-indigo-500 w-8 h-8 flex items-center justify-center"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Documentation</h3>
                    <p className="text-sm text-gray-600 mb-4">Browse help articles</p>
                    <button className="bg-indigo-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-indigo-600 transition-colors cursor-pointer whitespace-nowrap">
                      View Docs
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Submit a Ticket</h3>
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
                      <input
                        type="text"
                        placeholder="Brief description of your issue"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                      <select className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm">
                        <option>Technical Issue</option>
                        <option>Billing Question</option>
                        <option>Feature Request</option>
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                      <textarea
                        rows={6}
                        placeholder="Provide detailed information about your issue"
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      className="bg-teal-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Submit Ticket
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}